<template>
	<div class="relation__diagram-container">
		<div class="relation__model-container">
			<FontAwesomeIcon
				class="relation__model-icon"
				:icon="props.module.icon" />
			<div class="relation__model-name">
				{{ camelCaseToLabel(props.module.displayName) }}
			</div>
		</div>
		<div class="relation__arrows-container">
			<div class="relation__relation-name-container">
				<a
					class="relation__relation-type"
					target="_blank"
					:href="relationTypeDocs[props.relation.relationType]">
					{{ relationTypeMap[props.relation.relationType] }}
				</a>
				<div class="relation__relation-name">{{ props.relation.displayName }}</div>
			</div>
			<div class="relation__relation-arrow">
				<div class="relation__relation-arrow-line"></div>
				<FontAwesomeIcon
					class="relation__relation-arrow-tip"
					:icon="arrowIconMap[props.relation.relationType]" />
			</div>
			<div
				v-if="props.inverseRelation"
				class="relation__relation-arrow relation__relation-arrow--inverse">
				<FontAwesomeIcon
					class="relation__relation-arrow-tip relation__relation-arrow-tip--inverse"
					:icon="arrowIconMap[props.inverseRelation.relationType]" />
				<div class="relation__relation-arrow-line"></div>
			</div>
			<div
				v-if="props.inverseRelation"
				class="relation__relation-name-container relation__relation-name-container--inverse">
				<a
					class="relation__relation-type"
					target="_blank"
					:href="relationTypeDocs[props.inverseRelation.relationType]">
					{{ relationTypeMap[props.inverseRelation.relationType] }}
				</a>
				<div class="relation__relation-name">{{ props.inverseRelation.displayName }}</div>
			</div>
		</div>
		<div class="relation__model-container">
			<FontAwesomeIcon
				class="relation__model-icon"
				:icon="props.inverseModule.icon" />
			<div class="relation__model-name">
				{{ camelCaseToLabel(props.inverseModule.displayName) }}
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { camelCaseToLabel } from '@codecannon/schemas/utils/strings'
import type { EnrichedModule } from '@codecannon/schemas/schemas/EnrichedModule'
import type { EnrichedRelation } from '@codecannon/schemas/schemas/EnrichedRelation'
import FontAwesomeIcon from '@/components/FontAwesomeIcon.vue'

const arrowIconMap = {
	BelongsTo: 'fal fa-arrow-right-long',
	HasMany: 'fal fa-split',
	BelongsToMany: 'fal fa-split',
	HasOne: 'fal fa-arrow-right-long',
}

const relationTypeMap = {
	BelongsTo: 'belongs to',
	HasMany: 'has many',
	BelongsToMany: 'belongs to many',
	HasOne: 'has one',
}

const relationTypeDocs = {
	BelongsTo: 'https://laravel.com/docs/11.x/eloquent-relationships#one-to-many-inverse',
	HasMany: 'https://laravel.com/docs/11.x/eloquent-relationships#one-to-many',
	BelongsToMany: 'https://laravel.com/docs/11.x/eloquent-relationships#many-to-many',
	HasOne: 'https://laravel.com/docs/11.x/eloquent-relationships#one-to-one',
}

const props = defineProps<{
	relation: EnrichedRelation
	inverseModule: EnrichedModule
	inverseRelation: EnrichedRelation | null
	module: EnrichedModule
}>()
</script>

<style scoped lang="scss">
.relation__diagram-container {
	padding: 40px;
	display: flex;
	width: 100%;
	align-items: center;
	gap: 10px;
	background: var(--p-surface-0);

	@media all {
		background: var(--p-surface-950);
	}

	.relation__model-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		width: 130px;
		height: 130px;
		justify-content: center;
		gap: 8px;
		border-radius: 20px;
		border: 1px solid var(--p-surface-100);

		@media all {
			border: 2px solid var(--p-surface-800);
		}

		i {
			font-size: 50px;
			color: var(--p-primary-500);
		}

		.relation__model-name {
			font-size: 16px;
		}
	}

	.relation__arrows-container {
		flex: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		position: relative;

		.relation__relation-name-container {
			display: flex;
			gap: 5px;
			margin-bottom: -4px;

			&--inverse {
				margin-top: -7px;
			}

			.relation__relation-name,
			.relation__relation-type {
				font-size: 18px;
			}

			.relation__relation-name {
				color: var(--p-primary-500);
				font-weight: 600;
			}

			.relation__relation-type {
				color: var(--p-blue-500);

				&:hover {
					text-decoration: underline;
				}
			}
		}

		.relation__relation-arrow {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 100%;
			margin-bottom: -2px;
			margin-top: -2px;

			.relation__relation-arrow-line {
				background: var(--p-surface-300);
				height: 2px;
				width: calc(100% - 30px);

				@media all {
					background: var(--p-surface-700);
				}
			}

			.relation__relation-arrow-tip {
				display: inline-flex;
				font-size: 30px;
				margin-left: -1px;
				color: var(--p-surface-300);

				@media all {
					color: var(--p-surface-700);
				}

				&--inverse {
					transform: rotate(180deg);
					margin-left: 0;
					margin-right: -1px;
				}
			}
		}
	}
}
</style>
